import { useSubscription } from "@apollo/client";
import { Popover, Spin, Table, message } from "antd";
import cuid from "cuid";
import { find, get, isNil } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { BsViewList } from "react-icons/bs";
import { IoDuplicateOutline } from "react-icons/io5";
import {
  MdAssignmentInd,
  MdContentCopy,
  MdDeleteOutline,
  MdOpenInBrowser,
  MdOpenInNew,
  MdOutlineEventBusy,
  MdOutlineLock,
  MdOutlineLockOpen,
} from "react-icons/md";
import Avatar from "shared/components/Avatar";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Pagination from "shared/components/Pagination";
import Select from "shared/components/Select";

import Status from "shared/components/Status";
import Tooltip from "shared/components/Tooltip";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { CREATE_QUALIFICATION } from "utils/api/graphql/mutations/qualifications";
import { formatParisDateTime } from "utils/helpers/date";
import { readLeadTypology } from "utils/helpers/leadTypology";
import { QUALIFICATIONS } from "utils/api/graphql/queries/qualifications";
import { USERS } from "utils/api/graphql/queries/users";
import { contactDisplayName, contactInitials } from "utils/helpers/contact";
import { getPrimaryPhoneFromTelecoms } from "utils/helpers/phone";
import { isManageableProjectInOrganizationScope } from "utils/helpers/project";
import { resolveProjectStatus } from "utils/helpers/projectStatus";
import {
  ACTIONS,
  HOT_LIST,
  PROCESS_TAGS as PROCESS_TAGS_STATUSES,
  PROJECTS_LIST_COLUMNS,
  PROJECTS_LIST_LOCKED_COLUMNS,
  PROJECTS_SCOPE,
  QUALIFIER_TAGS,
  SUBSCRIPTION_STATUSES,
  THEMES_PALETTE,
  USER_TYPES,
} from "utils/constants";
import ColumnsSettings from "./ColumnsSettings";
import HeatBadge from "./HeatBadge";
import LastSignal from "./LastSignal";
import SignalFeed from "./SignalFeed";
import useHotListRows from "./useHotListRows";
import { shouldShowResumeSubscription } from "./shouldShowResumeSubscription";
import classNames from "classnames";

const SIGNED_LIKE_PROCESS_TAGS = [
  PROCESS_TAGS_STATUSES.SIGNED,
  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
];

const LOCK_ICON = {
  [true]: <MdOutlineLock size={24} color={COLORS.C_GRAY} />,
  [false]: <MdOutlineLockOpen size={24} color={COLORS.C_GRAY} />,
};

const ProjectsTable = ({
  tracers,
  count,
  data,
  setValues,
  type,
  skip,
  onDuplicate,
  onDelete,
  onDetail,
  onOpenInTab,
  onAssign,
  isAssigning,
  refetch: refetchProjects,
  showDrawerSubscription,
  showDrawerSign,
  setContactId,
  setProject,
  setOpen,
  onChangeStatus,
  actions,
  pagination,
  matchStatusesWithProcess,
  user,
  scope,
  showScopeToggle = false,
  hotMode = false,
  hotSignalsFilter = {},
  showSignalFeed = false,
  paginationFootprint = "projectsList",
  hiddenColumns = [],
  saveHiddenColumns,
  columnsConfigLoading = false,
  isLeadTypologyEnabled = false,
}) => {
  const isOrganizationScope =
    showScopeToggle && scope === PROJECTS_SCOPE.ORGANIZATION;
  const isAdmin = get(user, "meta.type") === USER_TYPES.INSURANCE_ADMIN.type;
  const isAircallEnabled =
    get(user, "organization.config.aircall.enabled") === true;
  const isManageableProject = (project) => {
    if (!isOrganizationScope) return true;
    return isManageableProjectInOrganizationScope(project, user);
  };
  // The admin pilots the whole organization, so he reassigns and follows up on
  // every project, but he only opens the ones he authored, i.e. his duplicates.
  const canOpenProjectDetail = (project) =>
    !isAdmin || get(project, "author.id") === get(user, "id");
  const allFieldsNull = useMemo(
    () =>
      data.every(
        (item) =>
          !get(
            get(THEMES_PALETTE, get(item, "fields.processTemplate", "DEFAULT")),
            "NAME",
          ),
      ),
    [data],
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const FILTER = t("FILTER", { returnObjects: true });
  const LIST = t("LIST", { returnObjects: true });
  const { t: tContact } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });
  const viewContactCard = (contactId, project) => {
    setContactId(contactId);
    setProject(project);
    setOpen(true);
  };
  const copyToClipboard = (value) =>
    navigator.clipboard
      .writeText(value)
      .then(() => message.success(LIST.COPIED))
      .catch(() => message.error(LIST.COPY_FAILED));
  const { data: signalsData, refetch: refetchSignals } = useQuery(
    graphql.queries.HOT_LIST_SIGNALS,
    {
      skip: !showSignalFeed,
      fetchPolicy: "network-only",
      variables: { ...hotSignalsFilter, take: HOT_LIST.SIGNALS_TAKE },
    },
  );
  const [, setSearchParams] = useSearchParams();
  const openSignalProject = ({ projectId, contactId }) => {
    if (!contactId) return;
    const project = data.find((item) => item.id === projectId);
    if (project) {
      viewContactCard(contactId, project);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("openContact", contactId);
        next.set("projectId", projectId);
        return next;
      },
      { replace: true },
    );
  };
  const hotList = useHotListRows({
    data,
    hotMode,
    organizationId: get(user, "organization.id"),
    refetch: () => {
      if (typeof refetchProjects === "function") refetchProjects();
      if (showSignalFeed && typeof refetchSignals === "function") {
        refetchSignals();
      }
    },
  });
  const [qualifiers, setQualifiers] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [advisorOverrides, setAdvisorOverrides] = useState({});
  const [qualifyingContactIds, setQualifyingContactIds] = useState({});
  const qualifyingContactIdsRef = useRef({});
  const { data: qualifiersData } = useQuery(graphql.queries.QUALIFIERS);
  const qualifierList = useMemo(
    () => get(qualifiersData, "qualifiers.data") || [],
    [qualifiersData],
  );
  const qualificationOptions = useMemo(
    () =>
      qualifierList.map(({ id, name, description }) => ({
        value: id,
        label: name,
        popover: description,
      })),
    [qualifierList],
  );
  const [addQualification] = useMutation(CREATE_QUALIFICATION);

  const contactIds = useMemo(() => {
    if (!data) return [];
    return data
      .map(({ contact }) => contact?.id)
      .filter(Boolean)
      .sort();
  }, [data]);
  const { refetch } = useQuery(QUALIFICATIONS, {
    variables: {
      withDeleted: true,
      where: {
        AND: [
          { last: true },
          { OR: contactIds.map((id) => ({ contact: { id } })) },
        ],
      },
    },
    onCompleted: ({ qualifications }) => {
      const newQualifiers = {};
      qualifications?.data.forEach(({ qualifier, contact }) => {
        newQualifiers[contact?.id] = qualifier?.name;
      });

      setQualifiers(newQualifiers);
    },
  });

  const [refetchUsers] = useLazyQuery(USERS, {
    variables: {
      where: {
        isActive: true,
        OR: [
          ...(isAdmin
            ? [{ meta: { type: USER_TYPES.INSURANCE_ADMIN.type } }]
            : []),
          { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
          { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
          { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
          { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
          { meta: { type: USER_TYPES.BROKER_REPRESENTATIVE.type } },
        ],
      },
    },
    fetchPolicy: "network-only",
  });

  useSubscription(graphql.subscriptions.QUALIFICATION_ADDED, {
    skip: !isReady || contactIds.length === 0,
    variables: {
      withDeleted: true,
      where: {
        AND: [
          { last: true },
          { OR: contactIds.map((id) => ({ contact: { id } })) },
        ],
      },
    },
    onData: ({ data: subscriptionData }) => {
      const qualification = subscriptionData?.data?.qualificationAdded;
      const contactId = qualification?.contact?.id;
      const tag = qualification?.qualifier?.tag;

      if (!contactId) return;

      setQualifiers((prev) => ({
        ...prev,
        [contactId]: qualification?.qualifier?.name,
      }));

      const autoAssign = tag === QUALIFIER_TAGS.PURSUIT_RADIANCE;

      autoAssign &&
        refetchUsers().then(({ data: userData }) => {
          const users = userData?.users?.data ?? [];

          const extDistPartnerUser = users.find(
            (u) => u?.params?.extDistPartner === true,
          );

          const relatedProject = data.find(
            (project) => get(project, "contact.id") === contactId,
          );
          const assignTo =
            relatedProject &&
            extDistPartnerUser &&
            !get(relatedProject, "author.id");

          if (!assignTo) return;

          setAdvisorOverrides((prev) => ({
            ...prev,
            [contactId]: extDistPartnerUser.id,
          }));
          onAssign({
            projectId: relatedProject.id,
            projectFields: relatedProject.fields,
            authorId: extDistPartnerUser.id,
          });
        });

      refetch();
    },
  });

  useEffect(() => {
    if (data) setIsReady(true);
  }, [data]);

  const shouldTakeOverOnQualify = (project, qualifier) => {
    if (get(project, "author.id")) return false;

    const currentStatus = get(
      matchStatusesWithProcess(get(project, "statuses")),
      "processStatus",
    );
    return (
      get(qualifier, "tag") !== QUALIFIER_TAGS.PURSUIT_RADIANCE &&
      !SIGNED_LIKE_PROCESS_TAGS.includes(get(currentStatus, "tag"))
    );
  };

  const releaseQualifyingContact = (contactId) => {
    delete qualifyingContactIdsRef.current[contactId];
    setQualifyingContactIds((prev) => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
  };

  const qualifyProject = (project, qualifierId) => {
    const contactId = get(project, "contact.id");
    if (!contactId || qualifyingContactIdsRef.current[contactId]) return;

    const qualifier = find(qualifierList, { id: qualifierId });
    qualifyingContactIdsRef.current[contactId] = true;
    setQualifyingContactIds((prev) => ({ ...prev, [contactId]: true }));

    return addQualification({
      variables: {
        data: {
          id: cuid(),
          qualifier: { id: qualifierId },
          contact: { id: contactId },
        },
      },
    })
      .then(() => {
        setQualifiers((prev) => ({
          ...prev,
          [contactId]: get(qualifier, "name"),
        }));
        if (!shouldTakeOverOnQualify(project, qualifier)) return undefined;
        return onAssign({
          projectId: project.id,
          projectFields: get(project, "fields"),
          authorId: get(user, "id"),
        })
          .then(() => refetchProjects?.())
          .catch(() => undefined);
      })
      .catch(() => undefined)
      .finally(() => releaseQualifyingContact(contactId));
  };

  const renderAdvisorCell = (record, project) => {
    const statusData = matchStatusesWithProcess(get(project, "statuses"));
    const currentStatus = get(statusData, "processStatus");

    const contactId = get(project, "contact.id");
    const overrideAuthorId = advisorOverrides[contactId];

    const selectedAuthorId =
      overrideAuthorId && overrideAuthorId !== record?.id
        ? overrideAuthorId
        : record?.id;

    const hasTakenOver = get(project, "hasResponsible");
    const isDistributed = !!record && !hasTakenOver && selectedAuthorId;

    return (
      <div className="d-flex align--center gap-4">
        <div className="select_200">
          <Select
            type="async"
            value={selectedAuthorId}
            node={{
              query: USERS,
              variables: {
                take: null,
                exclude: ["telecoms", "attachments", "broker", "contact"],
                where: {
                  AND: [
                    { isActive: true },
                    {
                      OR: [
                        ...(isAdmin
                          ? [
                              {
                                meta: { type: USER_TYPES.INSURANCE_ADMIN.type },
                              },
                            ]
                          : []),
                        { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
                        {
                          meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type },
                        },
                        { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
                        { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
                        {
                          meta: {
                            type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                          },
                        },
                      ],
                    },
                    ...(overrideAuthorId ? [{ id: overrideAuthorId }] : []),
                  ],
                },
              },
              accessors: {
                root: "users.data",
                label: {
                  separator: " ",
                  keys: ["firstname", "lastname"],
                },
              },
            }}
            placeholder={FILTER.BROKER}
            onChange={(value) => {
              setAdvisorOverrides((prev) => ({
                ...prev,
                [contactId]: value,
              }));

              onAssign({
                projectId: project.id,
                authorId: value,
              }).then(() => refetchProjects?.());
            }}
            disabled={
              isAssigning[project?.id] ||
              SIGNED_LIKE_PROCESS_TAGS.includes(currentStatus?.tag)
            }
            loading={isAssigning[project?.id]}
          />
        </div>
        {record && hasTakenOver && (
          <span className="takeover-badge takeover-badge--active">
            {LIST.TAKEN_OVER}
          </span>
        )}
        {isDistributed && (
          <span className="takeover-badge takeover-badge--distributed">
            {LIST.DISTRIBUTED}
          </span>
        )}
        {get(project, "fields.isManualAssign") && (
          <MdAssignmentInd size={16} color={COLORS.C_PRIMARY} />
        )}
      </div>
    );
  };

  const readTypologyCell = (project) => {
    if (!isLeadTypologyEnabled)
      return { line: null, showsSupplierWording: false };

    const resolved = get(project, "fields.leadTypology");
    const typology = isNil(resolved) ? null : readLeadTypology({ resolved });

    if (!typology)
      return {
        line: (
          <span className="name--column__typology">
            {LIST.TYPOLOGY} : {LIST.TYPOLOGY_NOT_APPLICABLE}
          </span>
        ),
        showsSupplierWording: false,
      };

    const line = (
      <span className="name--column__typology">
        {LIST.TYPOLOGY} : {typology.label || LIST.TYPOLOGY_UNQUALIFIED}
      </span>
    );

    if (!typology.rawLabel) return { line, showsSupplierWording: false };

    return {
      line: (
        <Popover
          content={`${LIST.TYPOLOGY_SUPPLIER_WORDING} : ${typology.rawLabel}`}
        >
          {line}
        </Popover>
      ),
      showsSupplierWording: true,
    };
  };

  const columns = [
    {
      key: PROJECTS_LIST_COLUMNS.AVATAR,
      width: 48,
      // responsive: ["sm"],
      dataIndex: "contact",
      render: (contact, project) => {
        return (
          <>
            {get(project, "fields.weedoTrust.scoreTotal") ? (
              <span
                title={get(LIST, "WEEDO_TRUST_SCORE_TOOLTIP")}
                className={classNames("weedo-trust-score", {
                  green:
                    Number(get(project, "fields.weedoTrust.scoreTotal")) >= 90,
                  orange:
                    Number(get(project, "fields.weedoTrust.scoreTotal")) < 90,
                })}
              >
                <span>{get(project, "fields.weedoTrust.scoreTotal")}</span>
              </span>
            ) : (
              <Avatar
                mode={"text"}
                size={"medium"}
                value={contactInitials(contact)}
              />
            )}
          </>
        );
      },
      fixed: true,
    },
    {
      key: PROJECTS_LIST_COLUMNS.CLIENT,
      title: LIST.CLIENT,
      dataIndex: "contact",
      render: (contact, project) => {
        const { line: typologyLine, showsSupplierWording } =
          readTypologyCell(project);
        return (
          <div className="name--column">
            <div className="d-flex">
              {hotMode && (
                <HeatBadge
                  project={project}
                  movement={hotList.movements[project.id]}
                  decay={hotList.decays[project.id]}
                  translate={LIST.HOT_MODE}
                />
              )}
              <div
                className="name--client"
                onClick={() => viewContactCard(get(contact, "id"), project)}
              >
                {contactDisplayName(contact)}
              </div>
            </div>
            <span className="name--column__source">
              {t("LIST.SOURCE", {
                name:
                  get(project, "source.name") ||
                  get(project, "tracer.source.name") ||
                  get(project, "tracer.name"),
              })}
            </span>
            {!showsSupplierWording && get(project, "fields.contactType") && (
              <span className="name--column__type">
                Type : {get(project, "fields.contactType").toLowerCase()}
                <Tooltip title={LIST.COPY}>
                  <MdContentCopy
                    size={14}
                    className="name--column__copy"
                    onClick={() =>
                      copyToClipboard(get(project, "fields.contactType"))
                    }
                  />
                </Tooltip>
              </span>
            )}
            {typologyLine}
          </div>
        );
      },
      fixed: true,
    },
    ...(hotMode
      ? [
          {
            key: PROJECTS_LIST_COLUMNS.LAST_SIGNAL,
            title: LIST.LAST_SIGNAL,
            dataIndex: "heatDetails",
            render: (_, project) => (
              <LastSignal
                project={project}
                translate={LIST.HOT_MODE.LAST_SIGNAL}
                labels={LIST.HOT_MODE.FEED.TYPES}
              />
            ),
          },
        ]
      : []),
    {
      key: PROJECTS_LIST_COLUMNS.PHONE,
      title: LIST.PHONE,
      dataIndex: "contact",
      render: (contact) => (
        <div className="text-sec">
          {getPrimaryPhoneFromTelecoms(get(contact, "telecoms"))}
        </div>
      ),
    },
    ...(isAircallEnabled
      ? [
          {
            key: PROJECTS_LIST_COLUMNS.CALLS_COUNT,
            title: LIST.CALLS_COUNT,
            dataIndex: "contact",
            align: "center",
            render: (contact) => (
              <Tooltip title={tContact("DRAWER.CALL_COUNTER_TOOLTIP")}>
                <span className="contact__call-counter">
                  {tContact("DRAWER.CALL_COUNTER", {
                    count: get(contact, "countedCallsCount", 0),
                  })}
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
    ...(allFieldsNull
      ? []
      : [
          {
            key: PROJECTS_LIST_COLUMNS.OFFER,
            title: LIST.OFFER,
            dataIndex: "fields",
            render: (fields) => {
              const text = get(
                get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                "NAME",
              );
              return text ? (
                <Status
                  status="gray"
                  style={{
                    color: get(
                      get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                      "COLOR",
                    ),
                    background: get(
                      get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                      "BACKGROUND",
                    ),
                  }}
                  text={text}
                />
              ) : null;
            },
          },
        ]),
    {
      key: PROJECTS_LIST_COLUMNS.STATUS,
      title: LIST.STATUS,
      dataIndex: "statuses",
      render: (record, project) => {
        const statusData = matchStatusesWithProcess(record);
        const { processStatus: currentStatus } = resolveProjectStatus(
          { statuses: record },
          tracers,
        );
        const statusText =
          project.expired && currentStatus?.tag !== PROCESS_TAGS_STATUSES.CLOSE
            ? PROCESS_TAGS.EXPIRED
            : get(currentStatus, "name", PROCESS_TAGS.IN_PROGRESS);

        const statusesOptions = get(
          statusData,
          "tracer.flow.process.processStatuses",
          [],
        )
          .filter(({ tag }) =>
            [
              PROCESS_TAGS_STATUSES.SIGNED,
              PROCESS_TAGS_STATUSES.CLOSE,
            ].includes(tag),
          )
          .map((status) => {
            const tag = get(status, "tag");
            return {
              label: get(status, "name"),
              value: get(status, "id"),
              onClick: [PROCESS_TAGS_STATUSES.SIGNED].includes(status.tag)
                ? () =>
                    showDrawerSign({
                      id: project.id,
                      statuses: project.statuses,
                    })
                : () =>
                    onChangeStatus({
                      project,
                      status,
                    }),
              disabled:
                ([
                  PROCESS_TAGS_STATUSES.SIGNED,
                  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
                  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
                ].includes(status.tag) &&
                  !project.locked) ||
                (tag === PROCESS_TAGS_STATUSES.SIGNED && project?.expired),
              popover:
                [
                  PROCESS_TAGS_STATUSES.SIGNED,
                  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
                  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
                ].includes(status.tag) && !project.locked
                  ? LIST.STATUS_POPOVER
                  : "",
            };
          });

        return ![
          PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
          PROCESS_TAGS_STATUSES.SIGNED,
          PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
        ].includes(get(currentStatus, "tag")) ? (
          <div className="select_200">
            <Select
              popupMatchSelectWidth={false}
              type="tag"
              value={statusText}
              options={statusesOptions}
            />
          </div>
        ) : (
          <Status
            type="custom"
            style={{
              color: get(currentStatus, "color"),
              background: get(currentStatus, "background"),
            }}
            text={statusText}
          />
        );
      },
    },
    {
      key: PROJECTS_LIST_COLUMNS.LAST_ACTION,
      title: LIST.QUALIFICATION,
      dataIndex: "contact",
      render: (contact, project) => {
        const contactId = get(contact, "id");
        const qualification = qualifiers[contactId] || LIST.EMPTY_QUALIFICATION;
        const isQualifying = !!qualifyingContactIds[contactId];
        if (!isManageableProject(project)) {
          return (
            <div className="text-sec projects-list-qualification">
              {qualification}
            </div>
          );
        }
        return (
          <div className="select_200">
            <Select
              popupMatchSelectWidth={false}
              type="tag"
              value={qualification}
              onSelect={(qualifierId) => qualifyProject(project, qualifierId)}
              options={qualificationOptions}
              disabled={!contactId || isQualifying}
              loading={isQualifying}
            />
          </div>
        );
      },
    },
    {
      key: PROJECTS_LIST_COLUMNS.CREATION_DATE,
      title: LIST.CREATION_DATE,
      dataIndex: "createdDate",
      render: (record) => formatParisDateTime(record),
    },
    {
      key: PROJECTS_LIST_COLUMNS.ADVISOR,
      title: LIST.ADVISOR,
      dataIndex: "author",
      render: (record, project) => {
        if (!isManageableProject(project)) {
          return (
            <div className="text-sec">
              {`${get(record, "firstname", "")} ${get(record, "lastname", "")}`}
            </div>
          );
        }
        return renderAdvisorCell(record, project);
      },
    },
    {
      key: PROJECTS_LIST_COLUMNS.LOCK,
      width: 62,
      dataIndex: "locked",
      align: "right",
      render: (record, row) => (
        <div className="d-flex justify--end">
          {row.expired && (
            <Popover content={LIST.EXPIRED_POPOVER}>
              <MdOutlineEventBusy size={24} color={COLORS.C_DANGER} />
            </Popover>
          )}
          {record && (
            <Popover
              content={row.expired ? LIST.EXPIRED_LOCKED : LIST.LOCKED_POPOVER}
            >
              {LOCK_ICON[record]}
            </Popover>
          )}
        </div>
      ),
    },
    ...(actions[ACTIONS.UPDATE_SUBSCRIPTION] ||
    actions[ACTIONS.DELETE_PROJECT] ||
    actions[ACTIONS.DUPLICATE_PROJECT] ||
    actions[ACTIONS.UPDATE_PROJECT]
      ? [
          {
            key: PROJECTS_LIST_COLUMNS.ACTIONS,
            title: LIST.ACTIONS,
            dataIndex: "id",
            align: "right",
            render: (id, project) => {
              const statusData = matchStatusesWithProcess(
                get(project, "statuses", []),
              );
              const currentStatus = get(statusData, "processStatus");
              const disableDetail =
                currentStatus?.tag === PROCESS_TAGS_STATUSES.CLOSE;
              const isSigned = get(project, "subscriptions", [])?.some(
                (subscription) =>
                  get(subscription, "status") === SUBSCRIPTION_STATUSES.SIGNED,
              );
              const canManageProject = isManageableProject(project);
              return (
                <div className="d-flex justify--end">
                  {shouldShowResumeSubscription({
                    project,
                    canManageProject,
                    canUpdateSubscription: actions[ACTIONS.UPDATE_SUBSCRIPTION],
                  }) && (
                    <Button
                      type="primary--light"
                      icon={<BsViewList />}
                      onClick={() => showDrawerSubscription(id)}
                    >
                      {LIST.RESUME_SUBSCRIPTION}
                    </Button>
                  )}

                  {actions[ACTIONS.DUPLICATE_PROJECT] && (
                    <Popover content={LIST.DUPLICATE}>
                      <IoDuplicateOutline
                        size={24}
                        onClick={() => onDuplicate(project)}
                      />
                    </Popover>
                  )}

                  {canManageProject && actions[ACTIONS.DELETE_PROJECT] && (
                    <Popover content={LIST.DELETE}>
                      <MdDeleteOutline
                        color={
                          isSigned
                            ? COLORS.C_DANGER_BACKGROUND
                            : COLORS.C_DANGER
                        }
                        size={24}
                        onClick={() => (isSigned ? null : onDelete(id))}
                      />
                    </Popover>
                  )}
                  {canManageProject &&
                    canOpenProjectDetail(project) &&
                    actions[ACTIONS.PROJECT] &&
                    actions[ACTIONS.UPDATE_PROJECT] && (
                      <>
                        <Popover content={LIST.OPEN_IN_TAB}>
                          <MdOpenInBrowser
                            color={
                              disableDetail
                                ? COLORS.C_PRIMARY_BACKGROUND
                                : COLORS.C_PRIMARY
                            }
                            size={24}
                            onClick={() =>
                              disableDetail ? null : onOpenInTab(project)
                            }
                          />
                        </Popover>
                        <Popover content={LIST.ACCESS}>
                          <MdOpenInNew
                            color={
                              disableDetail
                                ? COLORS.C_PRIMARY_BACKGROUND
                                : COLORS.C_PRIMARY
                            }
                            size={24}
                            onClick={() =>
                              disableDetail ? null : onDetail(project)
                            }
                          />
                        </Popover>
                      </>
                    )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const columnOptions = columns
    .filter(({ key }) => key !== PROJECTS_LIST_COLUMNS.AVATAR)
    .map(({ key }) => ({
      value: key,
      label: get(LIST.COLUMNS_SETTINGS.LABELS, key),
      disabled: PROJECTS_LIST_LOCKED_COLUMNS.includes(key),
    }));

  const visibleColumns = columns.filter(
    ({ key }) =>
      PROJECTS_LIST_LOCKED_COLUMNS.includes(key) ||
      !hiddenColumns.includes(key),
  );

  if (columnsConfigLoading) {
    return (
      <Card type={"simple"}>
        <div className="d-flex justify--center">
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card type={"simple"}>
      <div className="columns-settings">
        <ColumnsSettings
          options={columnOptions}
          hiddenColumns={hiddenColumns}
          onChange={saveHiddenColumns}
          translate={LIST.COLUMNS_SETTINGS}
        />
      </div>
      <div className={classNames({ "hot-list-layout": showSignalFeed })}>
        <div className="hot-list-table" {...hotList.hoverHandlers}>
          {hotList.isFrozen && (
            <div className="hot-list-table__frozen">{LIST.HOT_MODE.FROZEN}</div>
          )}
          <Table
            dataSource={hotList.rows}
            columns={visibleColumns}
            className="table__wrapper"
            rowKey={"id"}
            rowClassName={hotList.rowClassName}
            pagination={false}
            scroll={{ x: true }}
            tableLayout="auto"
          />
        </div>
        {showSignalFeed && (
          <SignalFeed
            signals={get(signalsData, "hotListSignals", []) || []}
            onSelect={openSignalProject}
          />
        )}
      </div>
      <Pagination
        key={paginationFootprint}
        footprint={paginationFootprint}
        count={count}
        setValues={setValues}
        skip={skip || 0}
        take={pagination.take}
      />
    </Card>
  );
};

export default ProjectsTable;
